import { PageTitle as SharedPageTitle } from '../components/layout/PageHeader';
import styled from 'styled-components';
import { useEffect } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useComplianceDocument } from '../hooks/useApi';
import { ComplianceForm } from '../features/compliance/components/ComplianceForm';
import { setLastComplianceRoute } from '../features/compliance/lib/navigation';

const PageContainer = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 52rem;
  margin: 0;
  padding: 0;
`;

const Header = styled.div`
  margin-bottom: var(--space-lg);
`;

const Title = styled(SharedPageTitle)``;

const Subtitle = styled.p`
  margin: 0.5rem 0 0;
  max-width: 65ch;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--text-muted);
`;

export const ComplianceFormPage = () => {
  const navigate = useNavigate();
  const { documentId } = useParams<{ documentId?: string }>();
  const isEdit = Boolean(documentId);
  const location = useLocation();

  useEffect(() => {
    setLastComplianceRoute(location.pathname);
  }, [location.pathname]);

  const { data, isLoading, error, refetch } = useComplianceDocument(
    isEdit ? documentId : null,
  );

  if (isEdit && !documentId) {
    return <Navigate to="/compliance" replace />;
  }

  return (
    <PageContainer>
      <Header>
        <Title>
          {isEdit ? 'Editar documento de cumplimiento' : 'Nuevo documento'}
        </Title>
        <Subtitle>
          Completa los datos del documento y adjunta el respaldo requerido.
        </Subtitle>
      </Header>

      {isEdit && isLoading ? (
        <Card>
          <LoadingSpinner message="Cargando documento..." />
        </Card>
      ) : isEdit && (error || !data) ? (
        <Card>
          <p style={{ margin: 0, color: 'var(--danger-600)' }}>
            No se pudo cargar el documento.
          </p>
          <div style={{ marginTop: 'var(--space-md)' }}>
            <Button variant="secondary" onClick={() => refetch()}>
              Reintentar
            </Button>
          </div>
        </Card>
      ) : (
        <ComplianceForm
          mode={isEdit ? 'edit' : 'create'}
          {...(data ? { document: data } : {})}
          onCancel={() =>
            navigate(isEdit ? `/compliance/${documentId}` : '/compliance', {
              ...(isEdit ? {} : { state: { skipComplianceResume: true } }),
            })
          }
          onSuccess={(id) => navigate(`/compliance/${id}`)}
        />
      )}
    </PageContainer>
  );
};

ComplianceFormPage.displayName = 'ComplianceFormPage';
